import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, Fragment } from 'react';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloQueryResult } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { type UseQueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { FeedType } from '../useFeedTypeSelector.ts';
import { isVisiblePopularFeedNode } from '../feedVisibility.ts';
import { FETCH_MORE_PAGE_SIZE } from '../feedConstants.ts';
import { MobileAppPromo } from '../MobileAppPromo/index.ts';
import type {
	usePopularFeedWithTimeGranularityStorageQuery$data,
	usePopularFeedWithTimeGranularityStorageQuery$variables,
} from '../__generated__/usePopularFeedWithTimeGranularityStorageQuery.graphql.ts';
import type { PopularFeedTimeGranularityValue } from '../Feed.tsx';

import { PopularFeedCard } from './PopularFeedCard/index.ts';
import { FeedCardsWrapper } from './FeedCardsWrapper.tsx';
import { ExitFeedAllyButton } from './ExitFeedAllyButton.tsx';

type Props = {
	popularFeedQuery: UseQueryInteropSupportedResult<
		usePopularFeedWithTimeGranularityStorageQuery$data,
		usePopularFeedWithTimeGranularityStorageQuery$variables
	>;
	handleFocusRef: () => void;
	popularFeedTimeGranularity: PopularFeedTimeGranularityValue;
};

// export for testing
export function deduplicateResults(results: any[]): {
	deduplicatedResults: any[];
	removedResults: { index: number; id: string }[];
} {
	const deduplicatedResultsMap = new Map<string, any>();
	const removedResultsMap = new Map<string, any>();
	results.forEach((item, index) => {
		const id = item?.content?.id;
		if (!id) return;
		if (deduplicatedResultsMap.has(id)) {
			removedResultsMap.set(id, { index, id });
		} else {
			deduplicatedResultsMap.set(id, item);
		}
	});

	return {
		deduplicatedResults: Array.from(deduplicatedResultsMap.values()),
		removedResults: Array.from(removedResultsMap.values()),
	};
}

export const PopularFeedCards: FC<Props> = ({
	popularFeedQuery,
	handleFocusRef,
	popularFeedTimeGranularity,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, loading, error, networkStatus, fetchMore } = popularFeedQuery;
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	const hasNextPage = fg('confluence_ai_feeds_home_banner')
		? false
		: data?.popularFeed?.pageInfo?.hasNextPage;
	const endCursor = data?.popularFeed?.pageInfo?.endCursor;

	const allResults = data?.popularFeed?.nodes;
	const results = useMemo(() => (allResults ?? []).filter(isVisiblePopularFeedNode), [allResults]);

	const { deduplicatedResults, removedResults } = deduplicateResults(results);
	// Build a stable Set of removed content IDs to use as a useEffect dependency
	// instead of JSON.stringify(removedResults), which serializes on every render.
	const removedResultIds = removedResults.map(({ id }) => id).join(',');
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	const finalResults = fg('confluence_ai_feeds_home_banner')
		? deduplicatedResults.slice(0, 3)
		: deduplicatedResults;

	// When the Popular empty-state banner is shown (feed empty + experiment on), it should be the
	// only promoted surface in the slot, so suppress the mobile app promo above it. Gated behind
	// the experiment so legacy behaviour is preserved for users not in the experiment; the
	// experiment call is last so exposure only fires for users with an empty Popular feed.
	const shouldSuppressMobilePromoForEmptyStateBanner =
		finalResults.length === 0 &&
		expValEquals('cc_mohiti_for_you_empty_states_exp', 'isEnabled', true);

	const loadNextPage = useCallback(async (): Promise<
		ApolloQueryResult<usePopularFeedWithTimeGranularityStorageQuery$data> | undefined
	> => {
		try {
			if (!endCursor) {
				return undefined;
			}

			const newResult = await fetchMore({
				variables: {
					first: FETCH_MORE_PAGE_SIZE,
					after: endCursor,
					timeGranularity: popularFeedTimeGranularity,
				},
				updateQuery: (
					prevResults: usePopularFeedWithTimeGranularityStorageQuery$data,
					{ fetchMoreResult },
				) => {
					if (!fetchMoreResult || !fetchMoreResult.popularFeed) {
						return prevResults;
					}

					// Deep clone required — a shallow spread shares node references with Apollo's
					// InMemoryCache and corrupts the cache write on fetchMore.
					const fetchMoreResultCopy = JSON.parse(JSON.stringify(fetchMoreResult));

					if (prevResults.popularFeed?.nodes) {
						fetchMoreResultCopy.popularFeed.nodes = prevResults.popularFeed.nodes.concat(
							fetchMoreResultCopy.popularFeed.nodes,
						);
					}

					return fetchMoreResultCopy;
				},
			});

			if (newResult.data) {
				const newVisibleCount =
					newResult.data?.popularFeed?.nodes.filter(isVisiblePopularFeedNode).length ?? 0;

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'paginated',
						actionSubject: 'popularFeed',
						source: 'homeScreen',
						attributes: {
							resultCount: newVisibleCount,
						},
					},
				}).fire();
			}

			if (newResult.errors) {
				throw newResult.errors;
			}

			return newResult;
		} catch (e) {
			throw e;
		}
	}, [fetchMore, createAnalyticsEvent, endCursor, popularFeedTimeGranularity]);

	useEffect(() => {
		removedResults.forEach(({ index, id }) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'rendered',
					actionSubject: 'duplicate',
					actionSubjectId: 'popularFeedCard',
					source: 'homeScreen',
					attributes: {
						ranking: index,
						contentId: id,
					},
				},
			}).fire();
		});
		// Only report when removed results change. We use a joined string of IDs
		// (removedResultIds) as a stable dependency instead of JSON.stringify(removedResults),
		// which would serialize the full array on every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [removedResultIds]);

	const disableAnimation = Boolean(
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR || (window.__SSR_RENDERED__ && networkStatus !== NetworkStatus.fetchMore),
	);

	return (
		<FeedCardsWrapper
			loading={loading}
			firstPageError={error}
			networkStatus={networkStatus}
			endCursor={endCursor}
			hasNextPage={hasNextPage}
			loadNextPage={loadNextPage}
			feedType={FeedType.POPULAR}
			hasResults={finalResults.length > 0}
		>
			{StatsigConfigurations.getBoolean('cc_mobile_app_promo_popular_feed_config', false) &&
				!shouldSuppressMobilePromoForEmptyStateBanner && <MobileAppPromo />}

			{finalResults.map((result, index) => {
				return (
					result && (
						<Fragment key={result.content.id}>
							<PopularFeedCard
								feedItem={result}
								ranking={index}
								disableAnimation={disableAnimation}
								timeGranularity={popularFeedTimeGranularity}
							/>
							<ExitFeedAllyButton
								onExit={handleFocusRef}
								source="popular-feed"
								feedCardIndex={index}
							/>
						</Fragment>
					)
				);
			})}
		</FeedCardsWrapper>
	);
};
